/**
 * MemoryConfigDialog
 * Extracted from AgentDetail. Edits memory policy + LLM/execution limits.
 */

import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

import type { AgentDetail as AgentDetailType } from '../../../types';

export interface MemoryConfigValues {
  memory_enabled: boolean;
  max_context_memories: number;
  max_memories_per_type: number;
  short_term_days: number;
  episodic_days: number;
  enable_semantic_search: boolean;
  auto_summarize: boolean;
  summarization_strategy: string;
  summarization_context_usage_threshold: number;
  keep_recent_context_ratio: number;
  fallback_context_window_tokens: number;
  inject_context_usage: boolean;
  max_context_messages: number;
  keep_recent_messages: number;
  resummarize_interval: number;
  temperature: number;
  max_tokens: number;
  max_iterations: number;
  delegation_timeout_seconds: number;
  tool_result_max_chars: number;
  max_tool_context_messages: number;
  tool_memory_enabled: boolean;
  tool_memory_recent_items: number;
  tool_memory_context_max_chars: number;
  tool_memory_max_per_session: number;
  tool_memory_max_per_agent: number;
}

const DEFAULTS: MemoryConfigValues = {
  memory_enabled: true,
  max_context_memories: 5,
  max_memories_per_type: 500,
  short_term_days: 7,
  episodic_days: 30,
  enable_semantic_search: false,
  auto_summarize: true,
  summarization_strategy: 'context_window',
  summarization_context_usage_threshold: 0.75,
  keep_recent_context_ratio: 0.25,
  fallback_context_window_tokens: 128000,
  inject_context_usage: true,
  max_context_messages: 20,
  keep_recent_messages: 10,
  resummarize_interval: 20,
  temperature: 0.7,
  max_tokens: 200000,
  max_iterations: 100,
  delegation_timeout_seconds: 900,
  tool_result_max_chars: 200000,
  max_tool_context_messages: 40,
  tool_memory_enabled: true,
  tool_memory_recent_items: 100,
  tool_memory_context_max_chars: 100000,
  tool_memory_max_per_session: 250000,
  tool_memory_max_per_agent: 2500000,
};

interface MemoryConfigDialogProps {
  open: boolean;
  agent: AgentDetailType;
  saving: boolean;
  onClose: () => void;
  onSubmit: (values: MemoryConfigValues) => void;
}

export const MemoryConfigDialog: React.FC<MemoryConfigDialogProps> = ({
  open,
  agent,
  saving,
  onClose,
  onSubmit,
}) => {
  const [form, setForm] = useState<MemoryConfigValues>(DEFAULTS);

  useEffect(() => {
    if (!open) return;
    const c = agent.config || {};
    const m = agent.memory_config || {};
    const semantic = m.semantic_search || {};
    setForm({
      memory_enabled: m.enabled !== false,
      max_context_memories: m.max_context_memories ?? DEFAULTS.max_context_memories,
      max_memories_per_type: m.max_memories_per_type ?? DEFAULTS.max_memories_per_type,
      short_term_days: m.short_term_days ?? DEFAULTS.short_term_days,
      episodic_days: m.episodic_days ?? DEFAULTS.episodic_days,
      enable_semantic_search:
        semantic.enabled ?? m.enable_semantic_search ?? c.enable_semantic_search ?? DEFAULTS.enable_semantic_search,
      auto_summarize: c.auto_summarize !== false,
      summarization_strategy:
        c.summarization_strategy ?? DEFAULTS.summarization_strategy,
      summarization_context_usage_threshold:
        c.summarization_context_usage_threshold ??
        DEFAULTS.summarization_context_usage_threshold,
      keep_recent_context_ratio:
        c.keep_recent_context_ratio ?? DEFAULTS.keep_recent_context_ratio,
      fallback_context_window_tokens:
        c.fallback_context_window_tokens ?? DEFAULTS.fallback_context_window_tokens,
      inject_context_usage: c.inject_context_usage !== false,
      max_context_messages: c.max_context_messages ?? DEFAULTS.max_context_messages,
      keep_recent_messages: c.keep_recent_messages ?? DEFAULTS.keep_recent_messages,
      resummarize_interval: c.resummarize_interval ?? DEFAULTS.resummarize_interval,
      temperature: c.temperature ?? DEFAULTS.temperature,
      max_tokens: c.max_tokens ?? DEFAULTS.max_tokens,
      max_iterations: c.max_iterations ?? DEFAULTS.max_iterations,
      delegation_timeout_seconds:
        c.delegation_timeout_seconds ?? DEFAULTS.delegation_timeout_seconds,
      tool_result_max_chars: c.tool_result_max_chars ?? DEFAULTS.tool_result_max_chars,
      max_tool_context_messages:
        c.max_tool_context_messages ?? DEFAULTS.max_tool_context_messages,
      tool_memory_enabled: c.tool_memory_enabled !== false,
      tool_memory_recent_items: c.tool_memory_recent_items ?? DEFAULTS.tool_memory_recent_items,
      tool_memory_context_max_chars:
        c.tool_memory_context_max_chars ?? DEFAULTS.tool_memory_context_max_chars,
      tool_memory_max_per_session:
        c.tool_memory_max_per_session ?? DEFAULTS.tool_memory_max_per_session,
      tool_memory_max_per_agent:
        c.tool_memory_max_per_agent ?? DEFAULTS.tool_memory_max_per_agent,
    });
  }, [open, agent]);

  const num = (key: keyof MemoryConfigValues, label: string, helper: string, min = 0, max = 100000) => (
    <TextField
      label={label}
      type="number"
      value={form[key] as number}
      onChange={(e) => setForm({ ...form, [key]: parseInt(e.target.value, 10) || 0 })}
      fullWidth
      helperText={helper}
      InputProps={{ inputProps: { min, max } }}
    />
  );

  const decimal = (key: keyof MemoryConfigValues, label: string, helper: string, min = 0, max = 2, step = 0.1) => (
    <TextField
      label={label}
      type="number"
      value={form[key] as number}
      onChange={(e) => setForm({ ...form, [key]: parseFloat(e.target.value) || 0 })}
      fullWidth
      helperText={helper}
      InputProps={{ inputProps: { min, max, step } }}
    />
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Memory &amp; execution policy</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <Typography variant="subtitle2">Durable memory</Typography>
          <FormControlLabel
            control={
              <Switch
                checked={form.memory_enabled}
                onChange={(e) => setForm({ ...form, memory_enabled: e.target.checked })}
              />
            }
            label="Inject and store durable agent memories"
          />
          {num('max_context_memories', 'Context memories', 'Maximum durable memories injected per execution', 0, 50)}
          {num('max_memories_per_type', 'Per-type memory cap', 'Maximum stored memories per type and write scope', 10, 10000)}
          {num('short_term_days', 'Short-term retention days', 'Expiry window for short-term memories', 1, 365)}
          {num('episodic_days', 'Episodic retention days', 'Expiry window for execution summary memories', 1, 3650)}
          <FormControlLabel
            control={
              <Switch
                checked={form.enable_semantic_search}
                onChange={(e) => setForm({ ...form, enable_semantic_search: e.target.checked })}
              />
            }
            label="Use embedding search for durable memories"
          />

          <Divider />
          <Typography variant="subtitle2">Conversation summarisation</Typography>
          <FormControlLabel
            control={
              <Switch
                checked={form.auto_summarize}
                onChange={(e) => setForm({ ...form, auto_summarize: e.target.checked })}
              />
            }
            label="Auto-summarise long conversations"
          />
          {form.auto_summarize ? (
            <Box sx={{ pl: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                select
                label="Summarisation trigger"
                value={form.summarization_strategy}
                onChange={(e) => setForm({ ...form, summarization_strategy: e.target.value })}
                fullWidth
                helperText="Context-window mode uses the selected model context window instead of message counts"
              >
                <MenuItem value="context_window">Context window</MenuItem>
                <MenuItem value="message_count">Message count</MenuItem>
              </TextField>
              {form.summarization_strategy === 'context_window' ? (
                <>
                  {decimal(
                    'summarization_context_usage_threshold',
                    'Context usage threshold',
                    'Prompt usage ratio that triggers auto-summarisation',
                    0.05,
                    0.95,
                    0.05
                  )}
                  {decimal(
                    'keep_recent_context_ratio',
                    'Recent context ratio',
                    'Fraction of the model window kept raw after summarisation',
                    0.05,
                    0.8,
                    0.05
                  )}
                  {num(
                    'fallback_context_window_tokens',
                    'Fallback context window',
                    'Used when the selected model has no context-window value',
                    1000,
                    2000000
                  )}
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.inject_context_usage}
                        onChange={(e) => setForm({ ...form, inject_context_usage: e.target.checked })}
                      />
                    }
                    label="Inject context usage into prompt"
                  />
                </>
              ) : (
                <>
                  {num('max_context_messages', 'Summarisation threshold', 'Messages before auto-summarisation triggers', 5, 100)}
                  {num('keep_recent_messages', 'Recent messages to keep', 'Sliding window size after summarisation', 2, 50)}
                  {num('resummarize_interval', 'Re-summarisation interval', 'New messages after a summary before summarising again', 1, 100)}
                </>
              )}
            </Box>
          ) : null}

          <Divider />
          <Typography variant="subtitle2">Tool memory</Typography>
          <FormControlLabel
            control={
              <Switch
                checked={form.tool_memory_enabled}
                onChange={(e) => setForm({ ...form, tool_memory_enabled: e.target.checked })}
              />
            }
            label="Remember compact tool-call outcomes"
          />
          {num('tool_memory_recent_items', 'Recent tool memories', 'Tool-memory rows injected into the next prompt', 0, 5000)}
          {num('tool_memory_context_max_chars', 'Tool-memory prompt budget', 'Maximum characters for injected tool memory', 500, 10000000)}
          {num('tool_memory_max_per_session', 'Tool memories per session', 'Retention cap for each conversation session', 10, 100000000)}
          {num('tool_memory_max_per_agent', 'Tool memories per agent', 'Retention cap for this agent and user', 100, 1000000000)}

          <Divider />
          <Typography variant="subtitle2">LLM &amp; execution limits</Typography>
          {decimal('temperature', 'Temperature', 'Sampling temperature for LLM calls', 0, 2, 0.1)}
          {num('max_tokens', 'Max output tokens', 'Maximum tokens the LLM can generate per response', 100, 200000)}
          {num('max_iterations', 'Max iterations', 'Tool-calling iterations before stopping (prevents loops)', 1, 100)}
          {num('delegation_timeout_seconds', 'Delegation timeout seconds', 'Seconds an AGENT expert waits for this agent before failing', 1, 86400)}
          {num('tool_result_max_chars', 'Tool result max chars', 'Truncation limit for tool results (prevents context overflow)', 500, 250000)}
          {num('max_tool_context_messages', 'Max tool context messages', 'Tool messages kept in context per execution', 5, 50)}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => onSubmit(form)} disabled={saving}>
          Save settings
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MemoryConfigDialog;
